import PinIcon from '@mui/icons-material/Pin';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useMemo } from 'react';
import { ReactComponent as AtoZIcon } from '../../svgs/a-to-z-sorting.svg';
import { ReactComponent as ZtoAIcon } from '../../svgs/z-to-a-sorting.svg';
import { connect } from 'react-redux';
import { setSortBy, setSortOrder } from '../../actions/searchbox';

/**
 * SortControl Component
 * Provides icon-based sorting controls for exhibitor and product listings
 * Features:
 * - Two sort options: Exhibitor Name and Booth Number
 * - Toggle between ascending and descending order
 * - Visual feedback with icon rotation for descending order
 * - Tooltips showing current sort state
 * - Configurable alignment (right/left/center)
 */
function SortControl(props) {
  const { sortBy, sortOrder, align = 'right' } = props;

  // Memoize styles so IconButton refs stay stable across re-renders.
  const iconStyleBoothNumber = useMemo(
    () => ({
      color: sortBy === 'booth_number' ? '#1976d2' : '#666',
      transition: 'all 0.3s ease',
      fontSize: '1.5rem',
    }),
    [sortBy]
  );

  const buttonStyleExhibitorName = useMemo(
    () => ({
      backgroundColor: sortBy === 'exhibitor_name' ? '#e3f2fd' : 'transparent',
      borderRadius: '4px',
      padding: '8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: sortBy === 'exhibitor_name' ? '#bbdefb' : '#f5f5f5',
      },
    }),
    [sortBy]
  );

  const buttonStyleBoothNumber = useMemo(
    () => ({
      backgroundColor: sortBy === 'booth_number' ? '#e3f2fd' : 'transparent',
      borderRadius: '4px',
      padding: '8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: sortBy === 'booth_number' ? '#bbdefb' : '#f5f5f5',
      },
    }),
    [sortBy]
  );

  const alphaIconStyle = useMemo(
    () => ({
      width: '24px',
      height: '24px',
      stroke: sortBy === 'exhibitor_name' ? '#1976d2' : '#666',
    }),
    [sortBy]
  );

  /**
   * Handle sort by exhibitor name
   * Toggles between ascending (A-Z) and descending (Z-A)
   */
  const handleSortByName = () => {
    if (sortBy === 'exhibitor_name') {
      // If already sorting by name, toggle the order
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      props.setSortOrder(newOrder);
    } else {
      // If sorting by something else, switch to name with ascending order
      props.setSortBy('exhibitor_name');
      props.setSortOrder('asc');
    }
  };

  /**
   * Handle sort by booth number
   * Toggles between ascending (Low to High) and descending (High to Low)
   */
  const handleSortByBooth = () => {
    if (sortBy === 'booth_number') {
      // If already sorting by booth number, toggle the order
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      props.setSortOrder(newOrder);
    } else {
      // If sorting by something else, switch to booth number with ascending order
      props.setSortBy('booth_number');
      props.setSortOrder('asc');
    }
  };

  /**
   * Get tooltip text for exhibitor name sort
   */
  const getNameTooltip = () => {
    if (sortBy === 'exhibitor_name') {
      return sortOrder === 'asc' 
        ? 'Sorted by Exhibitor Name (A-Z) - Click to sort Z-A' 
        : 'Sorted by Exhibitor Name (Z-A) - Click to sort A-Z';
    }
    return 'Sort by Exhibitor Name (A-Z)';
  };

  /**
   * Get tooltip text for booth number sort
   */
  const getBoothTooltip = () => {
    if (sortBy === 'booth_number') {
      return sortOrder === 'asc' 
        ? 'Sorted by Booth Number (Low to High) - Click to reverse' 
        : 'Sorted by Booth Number (High to Low) - Click to reverse';
    }
    return 'Sort by Booth Number (Low to High)';
  };

  const renderAlphaIcon = () => {
    const isActive = sortBy === 'exhibitor_name';
    const isAscending = sortOrder === 'asc';
    if (isActive && !isAscending) {
      return <AtoZIcon style={alphaIconStyle} />;
    }
    return <ZtoAIcon style={alphaIconStyle} />;
  };

  const alignmentStyles = {
    right: { justifyContent: 'flex-end' },
    left: { justifyContent: 'flex-start' },
    center: { justifyContent: 'center' }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        ...alignmentStyles[align]
      }}
      className="sort-control-container"
    >
      {/* Label */}
      <Box 
        sx={{ 
          fontSize: '0.875rem', 
          color: '#666',
          fontWeight: 500
        }}
      >
        Sort by:
      </Box>

      {/* Sort by Exhibitor Name */}
      <Tooltip title={getNameTooltip()} arrow>
        <IconButton
          onClick={handleSortByName}
          sx={buttonStyleExhibitorName}
          size="small"
          aria-label="Sort by exhibitor name"
        >
          {renderAlphaIcon()}
        </IconButton>
      </Tooltip>

      {/* Sort by Booth Number */}
      <Tooltip title={getBoothTooltip()} arrow>
        <IconButton
          onClick={handleSortByBooth}
          sx={buttonStyleBoothNumber}
          size="small"
          aria-label="Sort by booth number"
        >
          <PinIcon sx={iconStyleBoothNumber} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// Map Redux state to component props
const mapStateToProps = (state) => {
  return {
    sortBy: state.searchbox.sortBy,
    sortOrder: state.searchbox.sortOrder,
  };
};

// Map Redux actions to component props
const mapDispatchProps = (dispatch) => {
  return {
    setSortBy: (data) => dispatch(setSortBy(data)),
    setSortOrder: (data) => dispatch(setSortOrder(data)),
  };
};

export default connect(mapStateToProps, mapDispatchProps)(SortControl);

